import { useEffect, useRef, useState } from 'react'
import { StatusPill } from '@/components/ui'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import QueryStateGate from '@/components/QueryStateGate'
import { supabase } from '@/lib/supabase'
import { Search, X, Package, Loader2, MapPin, Camera } from 'lucide-react'
import BadgeCameraScanModal from '@/components/BadgeCameraScanModal'
import { makePalletDecoder } from '@/lib/palletCameraDecode'
import clsx from 'clsx'
import { formatGradeLabel } from '@/lib/gradeLabel'
import { isValidSscc, normaliseSscc } from '@/lib/sscc'
import { ssccMatchCandidates } from '@/lib/ssccLookup'
import { BIN_PAGE_MODULES } from '@/lib/modules'
import { palletWhereabouts, exactFinderPick } from '@/lib/coldroomFind'
import { useActiveColdrooms } from '@/hooks/useActiveColdrooms'
import { useModuleGate } from '@/hooks/useCanOpenModule'

// ---------------------------------------------------------------------------
// Shared-state plumbing — one modal, many buttons.
// Every PalletSearchButton triggers the single PalletSearchRoot via a custom
// window event so we never end up with duplicate modals or duplicate ⌘K
// listeners.
// ---------------------------------------------------------------------------
const OPEN_EVENT = 'kf49:pallet-search-open'

// Opens the pallet/bin modal. Exported so the ⌘K command palette (HelpBar)
// can delegate to it — the palette owns ⌘K now, and "Find pallet / bin…" in
// the palette calls this to open the pallet modal. `initial` is what was
// already typed or scanned into the palette (F09 UF-01): the finder opens
// with it in the box and searching, so the number is never typed twice.
export function openPalletSearch(initial = '') {
  window.dispatchEvent(new CustomEvent<PalletSearchOpenDetail>(OPEN_EVENT, { detail: { initial } }))
}
interface PalletSearchOpenDetail { initial: string }

// ---------------------------------------------------------------------------
// Types — search returns either a pallet or a bin, both routed by `kind`.
// ---------------------------------------------------------------------------
interface PalletHit {
  kind:          'pallet'
  id:            string                   // pallet_id
  display:       string                   // pallet_id (what the user typed against)
  status:        string
  net_weight_kg: number | null
  size_code:     string | null
  grade:         string | null
  packed_at:     string
  runLabel:      string                   // "R-2026-015 · M Smith · Lemons"
  /** Found by an exact barcode lane (ssc_id / received sscc) — Enter may open it. */
  viaSscc:       boolean
  /** Where it stands (F09 UF-02): the pallet's own location columns. */
  coldroom_id:       string | null
  coldroom_grid:     string | null
  coldroom_overflow: boolean | null
  loading_bay_lane:  string | null
  missionNumber:     string | null
}

interface BinHit {
  kind:          'bin'
  id:            string                   // bin_id
  display:       string                   // bin_number
  status:        string
  net_weight_kg: number
  size_code:     string | null
  grade:         string | null
  born_date:     string
  runLabel:      string                   // grower · variety (no run number to keep it short)
}

type SearchHit = PalletHit | BinHit

// Each side of the search reads at most this many rows (newest first).
const SIDE_CAP = 15

// Before a search has answered: no rows, nothing left out.
const NO_RESULT: { hits: SearchHit[]; capped: boolean } = { hits: [], capped: false }

/**
 * Where a hit opens for THIS viewer, or null when it opens nowhere — never a
 * page the route gate answers with Home (F05 UF-05). A bin opens its own page
 * for a viewer holding Bin Inventory, Rebin, Coldroom or QC (BIN_PAGE_MODULES, the
 * route gate's rule). `can` is useModuleGate's answer: null (still deciding)
 * counts as no.
 */
export function searchHitHref(hit: SearchHit, can: (moduleKey: string) => boolean | null): string | null {
  if (hit.kind === 'pallet') return `/pallets/${hit.id}`
  return BIN_PAGE_MODULES.some(k => can(k) === true) ? `/bins/${hit.id}` : null
}

/**
 * Whether the viewer can open a hit: 'open' (it has an href), 'deciding' (the
 * module gate has not answered yet), or 'closed' (it never will). A hit is
 * LISTED either way — leaving out a bin the viewer cannot open made the modal
 * say "No pallets or bins match" over a bin that exists, and while the gate
 * was still loading it said so even to a viewer who could open it.
 */
export type SearchHitAccess = 'open' | 'deciding' | 'closed'
export function searchHitAccess(hit: SearchHit, can: (moduleKey: string) => boolean | null): SearchHitAccess {
  if (searchHitHref(hit, can) != null) return 'open'
  if (hit.kind === 'bin' && BIN_PAGE_MODULES.some(k => can(k) === null)) return 'deciding'
  return 'closed'
}

const STATUS_LABEL: Record<string, string> = {
  IN_PRODUCTION:      'In production',
  IN_COLDROOM:        'Coldroom',
  IN_LOADING_AREA:    'Loading bay',
  INSPECTED_APPROVED: 'Approved',
  INSPECTED_REJECTED: 'Rejected',
  DISPATCHED:         'Dispatched',
  RETURNED_FRESHMARK: 'Returned',
  REBINNED:           'Awaiting placement',
  TIPPED:             'Tipped',
  CANCELLED:          'Cancelled',
}

// ---------------------------------------------------------------------------
// Result assembly. Kept out of the queryFn so that function stays about I/O:
// it now fans out four queries and hands the rows here. (Inlining these grew
// the queryFn past the complexity gate when the fourth lane was added.)
// ---------------------------------------------------------------------------

// Flatten the pallet-side result groups into hits, keeping the FIRST appearance
// of each pallet. Groups are passed in priority order — the two SSCC lanes
// before the pallet_id ilike — so a pallet found by the barcode the operator
// actually scanned is not displaced by the same pallet arriving from a looser
// text match.
function toPalletHits(groups: Array<any[] | null>, ssccGroups = 0): PalletHit[] {
  const seen = new Set<string>()
  const out: PalletHit[] = []
  for (const [gi, group] of groups.entries()) {
    for (const p of group ?? []) {
      if (seen.has(p.pallet_id)) continue
      seen.add(p.pallet_id)
      out.push(toPalletHit(p, gi < ssccGroups))
    }
  }
  return out
}

// A to-one embed arrives as an object or a one-row array, depending on the FK.
const firstOf = (v: any) => (Array.isArray(v) ? v[0] : v)
const orNull = <T,>(v: T | undefined): T | null => v ?? null
const runLabelOf = (run: any): string =>
  run ? `${run.run_number} · ${run.grower} · ${run.variety}` : 'Run missing'

function toPalletHit(p: any, viaSscc: boolean): PalletHit {
  return {
    kind:          'pallet',
    id:            p.pallet_id,
    display:       p.pallet_id,
    status:        p.status,
    net_weight_kg: p.net_weight_kg,
    size_code:     p.size_code,
    grade:         p.grade,
    packed_at:     p.packed_at,
    runLabel:      runLabelOf(firstOf(p.runs)),
    viaSscc,
    coldroom_id:       orNull(p.coldroom_id),
    coldroom_grid:     orNull(p.coldroom_grid),
    coldroom_overflow: orNull(p.coldroom_overflow),
    loading_bay_lane:  orNull(p.loading_bay_lane),
    missionNumber:     orNull(firstOf(p.mission)?.mission_number),
  }
}

// Where it stands rides on the same read (F09 UF-02): the location columns are
// the pallet's own, and the truck's number is one embed — so a row can never
// show a place while the search itself failed.
const PALLET_COLS = 'pallet_id, status, grade, size_code, net_weight_kg, packed_at, runs(run_number, grower, variety), '
  + 'coldroom_id, coldroom_grid, coldroom_overflow, loading_bay_lane, mission:loading_missions!pallets_loading_mission_id_fkey(mission_number)'

const noRows = () => Promise.resolve({ data: [] as any[], error: null })

// The exact ssc_id lane — only for a valid-looking SSCC (we minted these).
function ssccLane(trimmed: string) {
  return isValidSscc(trimmed)
    ? supabase.from('pallets').select(PALLET_COLS)
        .eq('ssc_id', normaliseSscc(trimmed)).is('deleted_at', null).limit(5)
    : noRows()
}

// The received-SSCC lane (`pallets.sscc`). ⚠️ Deliberately NOT gated on
// isValidSscc: these are other people's codes and a failing check digit is
// usually a legacy code, not a typo — gating skips exactly the rows this finds.
function receivedLane(rawTrimmed: string) {
  const candidates = ssccMatchCandidates(rawTrimmed)
  return candidates.length
    ? supabase.from('pallets').select(PALLET_COLS)
        .in('sscc', candidates).is('deleted_at', null).limit(5)
    : noRows()
}

function throwFirstError(results: Array<{ error: unknown }>) {
  for (const r of results) if (r.error) throw r.error
}

// A full side means older matches were left out.
const isFull = (rows: any[] | null) => (rows?.length ?? 0) >= SIDE_CAP

function toBinHits(rows: any[] | null): BinHit[] {
  return (rows ?? []).map((b: any) => ({
    kind:          'bin',
    id:            b.bin_id,
    display:       b.bin_number,
    status:        b.status,
    net_weight_kg: b.net_weight_kg,
    size_code:     b.size_code,
    grade:         b.grade,
    born_date:     b.born_date,
    runLabel:      `${b.grower} · ${b.variety}`,
  }))
}

// ---------------------------------------------------------------------------
// Root — mount once in App.tsx. Owns the modal state. Opened via OPEN_EVENT
// (the header button and the ⌘K palette's "Find pallet / bin…" entry). ⌘K
// itself now opens the HelpBar command palette, which delegates here.
// ---------------------------------------------------------------------------
export function PalletSearchRoot() {
  // `opened` counts opens: it keys the modal, so an open that arrives while
  // the finder is already up re-seeds the box with the new number rather than
  // keeping the old one.
  const [state, setState] = useState<{ opened: number; initial: string } | null>(null)

  // Any openPalletSearch() call (button or the ⌘K palette) dispatches OPEN_EVENT.
  useEffect(() => {
    function onOpen(e: Event) {
      const initial = (e as CustomEvent<PalletSearchOpenDetail | null>).detail?.initial ?? ''
      setState(s => ({ opened: (s?.opened ?? 0) + 1, initial }))
    }
    window.addEventListener(OPEN_EVENT, onOpen)
    return () => window.removeEventListener(OPEN_EVENT, onOpen)
  }, [])

  if (!state) return null
  return <SearchModal key={state.opened} initial={state.initial} onClose={() => setState(null)} />
}

// The old <PalletSearchButton/> header trigger is gone — the ⌘K command
// palette (HelpBar) now owns the shortcut and offers "Find pallet / bin…",
// which calls openPalletSearch() above to open this modal.

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------
// No camera API (insecure context, very old browser): no button.
function FinderCameraButton({ onOpen }: { onOpen: () => void }) {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices) return null
  return (
    <button type="button" onClick={onOpen}
      className="tap-target-y inline-flex items-center gap-1.5 px-3 rounded-lg border hairline-strong text-sm text-white hover:bg-surface shrink-0">
      <Camera size={16} /> Camera
    </button>
  )
}

function FinderCameraScanner({ open, onScanned, onClose }: { open: boolean; onScanned: (v: string) => void; onClose: () => void }) {
  if (!open) return null
  return (
    <BadgeCameraScanModal
      heading="Scan a pallet label"
      hint="Hold the pallet barcode steady in the frame"
      noun="pallet"
      makeDecoder={makePalletDecoder}
      onScanned={onScanned}
      onClose={onClose} />
  )
}

// React events bubble through the tree: the backdrop closes only on a tap on
// itself, so a tap inside the camera never reaches it.
function closeOnBackdrop(onClose: () => void) {
  return (e: React.MouseEvent) => { if (e.target === e.currentTarget) onClose() }
}

function SearchModal({ initial, onClose }: { initial: string; onClose: () => void }) {
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState(initial)
  const [activeIdx, setActiveIdx] = useState(0)
  const [showCamera, setShowCamera] = useState(false)

  useEffect(() => { inputRef.current?.focus() }, [])

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape' && !showCamera) onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, showCamera])

  // Debounce the fetch trigger: a 6-char type/scan fires 1 query, not 6. The
  // input stays bound to `query` (instant); only the search key is debounced.
  // Seeded with the palette's text so a carried number searches at once.
  const [debounced, setDebounced] = useDebounced(query, initial)
  const trimmed = debounced.trim().toUpperCase()
  // The received-SSCC lane is matched against what was CAPTURED, so it needs
  // the input before the uppercase fold (`]c1…` from a scanner would not
  // survive it). Keyed separately too — uppercasing is lossy, so two inputs
  // differing only in case share a `trimmed` but not a `rawTrimmed`.
  const rawTrimmed = debounced.trim()

  const searchQ = useQuery({
    queryKey: ['global-search', trimmed, rawTrimmed],
    enabled: trimmed.length >= 2,
    queryFn: async (): Promise<{ hits: SearchHit[]; capped: boolean }> => {
      // Fan out to pallets + bins in parallel. Each side caps at 15; we take
      // the most recent of each so the user gets a balanced view. A wedge
      // scanner reading a pallet's own GS1-128 barcode sends its SSCC, not
      // the pallet_id — an ilike on pallet_id never matches that, so a
      // valid-looking SSCC also gets an exact ssc_id lookup.
      // A fourth side matches the OTHER SSCC lane, `pallets.sscc` — a third
      // party's own label on a pallet we merely store, disjoint from the
      // ssc_id we mint (see src/lib/ssccLookup.ts). Its own query on its own
      // column rather than an .or() across both, which would lose the index on
      // each. ⚠️ Deliberately NOT gated on isValidSscc like the ssc_id side:
      // these are other people's codes and a failing check digit is usually a
      // legacy code, not a typo — gating skips exactly the rows this finds.
      // No unique constraint on the column, so the limit is a real cap.
      // Location columns + the truck embed: see PALLET_COLS.
      const [palletRes, binRes, ssccRes, receivedRes] = await Promise.all([
        supabase
          .from('pallets')
          .select(PALLET_COLS)
          .ilike('pallet_id', `%${trimmed}%`)
          .is('deleted_at', null)
          .order('packed_at', { ascending: false })
          .limit(SIDE_CAP),
        supabase
          .from('bins')
          .select('bin_id, bin_number, status, grade, size_code, net_weight_kg, born_date, grower, variety, coldroom_id, coldroom_grid, coldroom_overflow')
          .ilike('bin_number', `%${trimmed}%`)
          .is('deleted_at', null)
          .order('born_date', { ascending: false })
          .limit(SIDE_CAP),
        ssccLane(trimmed),
        receivedLane(rawTrimmed),
      ])
      throwFirstError([palletRes, binRes, ssccRes, receivedRes])

      // Barcode lanes first so a row found by its SSCC keeps its place ahead of
      // the same row arriving from the looser pallet_id ilike.
      const pallets = toPalletHits([ssccRes.data, receivedRes.data, palletRes.data], 2)
      const bins    = toBinHits(binRes.data)

      // Exact-match-first ordering — if the user scanned a code, we want the
      // exact hit at the top regardless of type.
      const exactKey = (h: SearchHit) => h.display.toUpperCase() === trimmed ? 0 : 1
      const hits = [...pallets, ...bins].sort((a, b) => exactKey(a) - exactKey(b))
      // A full side means older matches were left out: say so, rather than
      // let a short list read as every match there is.
      const capped = isFull(palletRes.data) || isFull(binRes.data)
      return { hits, capped }
    },
  })
  const gate = useModuleGate()
  const { hits: results, capped } = searchQ.data ?? NO_RESULT
  const { isFetching } = searchQ

  const { roomCode, roomsUnread } = useRoomCodes(results)

  function go(hit: SearchHit) {
    const href = searchHitHref(hit, gate)
    if (!href) return
    navigate(href, hit.kind === 'pallet' ? undefined
      : { state: { returnTo: `${window.location.pathname}${window.location.search}` } })
    onClose()
  }

  // Enter has ONE rule, whatever the timing (F09 UF-02): it opens the row that
  // IS the number in the box — pallet id, then SSCC, then bin — and nothing
  // else. A scanner's Enter lands inside the 250 ms debounce, so Enter skips
  // the wait and searches the box as it is now; the exact row opens once that
  // answer is in. A partial number keeps the list up, whether the list had
  // settled or not — the highlighted row is never opened as a guess. A row the
  // operator picked with ↑↓ is a choice, not a guess: Enter opens that one.
  const setOpenOnArrival = useOpenOnArrival(trimmed, searchQ, go)
  const [picked, setPicked] = useState(false)

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    const key = e.key
    if (key === 'ArrowDown' || key === 'ArrowUp') {
      e.preventDefault()
      setActiveIdx(i => movedIdx(key, i, results.length))
      setPicked(results.length > 0)
    } else if (key === 'Enter') {
      e.preventDefault()
      enterPressed()
    }
  }

  function enterPressed() {
    const now = query.trim()
    if (now.length < 2) return
    const settled = debounced === query && !isFetching
    const chosen = picked && settled ? results[activeIdx] : undefined
    if (chosen) { go(chosen); return }
    // After a FAILED search the same text keeps the same query key, so
    // setDebounced below changes nothing and Enter would do nothing at all:
    // ask again.
    if (searchQ.isError && debounced === query) void searchQ.refetch()
    setDebounced(query)
    setOpenOnArrival(now.toUpperCase())
  }

  // A camera read runs exactly like a hand scanner's scan: the number lands in
  // the box and Enter's rule applies at once (whole number opens, partial lists).
  function closeCamera() {
    setShowCamera(false)
    // Chrome leaves focus on the tapped Camera button: a wedge scan would type
    // into nothing and its Enter would reopen the camera.
    inputRef.current?.focus()
  }

  function cameraScanned(value: string) {
    closeCamera()
    // Same retry as Enter: a failed search keeps its query key, so re-scanning
    // the same value must ask again.
    if (searchQ.isError && debounced === value) void searchQ.refetch()
    setQuery(value)
    setActiveIdx(0)
    setPicked(false)
    setDebounced(value)
    setOpenOnArrival(value.toUpperCase())
  }

  return (
    <div
      data-theme="dark"
      className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center pt-16 sm:pt-24 px-4"
      onClick={closeOnBackdrop(onClose)}
    >
      <div
        className="w-full max-w-xl surface-sunk rounded-2xl shadow-2xl border hairline-strong overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b hairline">
          <Search size={18} className="text-fg-muted shrink-0" />
          <input
            ref={inputRef}
            type="text"
            aria-label="Pallet ID or bin number"
            value={query}
            onChange={e => { setQuery(e.target.value); setActiveIdx(0); setPicked(false); setOpenOnArrival(null) }}
            onKeyDown={handleKeyDown}
            placeholder="Scan or type a pallet ID or bin # — e.g. 2026-00012 / RBN-2026-00007"
            className="flex-1 min-w-0 bg-transparent text-white text-base font-mono uppercase placeholder:text-fg-placeholder placeholder:normal-case placeholder:font-sans focus:outline-none"
            autoComplete="off"
            spellCheck={false}
          />
          {isFetching && <Loader2 size={16} className="animate-spin text-fg-muted shrink-0" />}
          <FinderCameraButton onOpen={() => setShowCamera(true)} />
          <button onClick={onClose} aria-label="Close pallet search"
            className="tap-target p-1 -m-1 rounded-lg hover:bg-surface shrink-0">
            <X size={18} className="text-fg-muted" />
          </button>
        </div>

        <div className="max-h-[60dvh] overflow-y-auto">
          <FinderEmptyState trimmed={trimmed} isFetching={isFetching} noResults={results.length === 0} query={searchQ} />

          {results.map((r, i) => (
            <SearchHitRow key={`${r.kind}:${r.id}`} hit={r} gate={gate} roomCode={roomCode} isActive={i === activeIdx}
              onOpen={() => go(r)} onHover={() => setActiveIdx(i)} />
          ))}
        </div>

        <FinderNotices capped={capped} roomsUnread={roomsUnread} />

        <FinderKeysHint show={results.length > 0} />
      </div>
      <FinderCameraScanner open={showCamera} onScanned={cameraScanned} onClose={closeCamera} />
    </div>
  )
}

// What the list area says when there is no list: the two-character prompt, or
// a no-match / failed-search card.
function FinderEmptyState({ trimmed, isFetching, noResults, query }: {
  trimmed: string; isFetching: boolean; noResults: boolean
  query: React.ComponentProps<typeof QueryStateGate>['query']
}) {
  if (trimmed.length < 2) return (
    <div className="px-6 py-8 text-center text-sm text-fg-muted">
      <Package size={28} className="mx-auto mb-2 opacity-40" />
      Type at least two characters to search
      <br />
      <span className="text-xs opacity-70">Pallets, bins — last few digits work too</span>
    </div>
  )
  if (isFetching || !noResults) return null
  /* "No match" is a claim about the data. On a failed or paused search it is a
     claim about the network, and the operator scanning a pallet at the door has
     no way to tell the two apart — they conclude the pallet does not exist. */
  return (
    <div className="px-6 py-8 text-center text-sm">
      <QueryStateGate query={query} noun="pallets or bins"
        empty={
          <span className="text-fg-muted">
            No pallets or bins match <span className="font-mono text-fg-secondary">{trimmed}</span>
          </span>
        } />
    </div>
  )
}

// The search key trails the box by 250 ms; the setter lets Enter skip the wait.
function useDebounced(value: string, initial: string) {
  const [debounced, setDebounced] = useState(initial)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), 250)
    return () => clearTimeout(t)
  }, [value])
  return [debounced, setDebounced] as const
}

// Holds the number Enter asked for until ITS answer is in, then opens the row
// that is that number. `hits` is undefined until a search has answered. A
// failed search disarms it: a later Retry that succeeds must not navigate on
// its own, long after the Enter it answered.
function useOpenOnArrival(
  trimmed: string,
  q: { isFetching: boolean; isError: boolean; data?: { hits: SearchHit[] } },
  go: (hit: SearchHit) => void,
) {
  const [openOnArrival, setOpenOnArrival] = useState<string | null>(null)
  const { isFetching, isError } = q
  const hits = q.data?.hits
  useEffect(() => {
    if (!openOnArrival || trimmed !== openOnArrival || isFetching) return
    if (isError) { setOpenOnArrival(null); return }
    if (!hits) return
    setOpenOnArrival(null)
    const hit = exactFinderPick(hits, openOnArrival)
    if (hit) go(hit)
    // `go` is a fresh closure each render; the answer's arrival is the trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openOnArrival, trimmed, isFetching, isError, hits])
  return setOpenOnArrival
}

function FinderKeysHint({ show }: { show: boolean }) {
  if (!show) return null
  return (
    <div className="border-t hairline px-4 py-2 text-[11px] text-fg-muted flex items-center gap-3">
      <span>↑↓ navigate</span>
      <span>↵ open</span>
      <span>esc close</span>
    </div>
  )
}

function movedIdx(key: string, i: number, count: number): number {
  return key === 'ArrowDown' ? Math.min(i + 1, count - 1) : Math.max(i - 1, 0)
}

// Room codes for "Coldroom CR2 · grid B7". Until they are read — or if the
// read fails — a row says "Coldroom · grid B7", the pallet page's own words,
// never a guessed room; a failed read says so under the list.
function useRoomCodes(results: SearchHit[]) {
  const roomsQ = useActiveColdrooms()
  const roomCode = (id: string) => roomsQ.data?.find(r => r.coldroom_id === id)?.code ?? null
  const roomsUnread = roomsQ.data === undefined && roomsQ.isError
    && results.some(r => r.kind === 'pallet' && r.status === 'IN_COLDROOM')
  return { roomCode, roomsUnread }
}

// What the list is NOT showing: a capped side, or rooms that could not be read.
function FinderNotices({ capped, roomsUnread }: { capped: boolean; roomsUnread: boolean }) {
  if (!capped && !roomsUnread) return null
  return (
    <div className="border-t hairline px-4 py-2 text-xs text-amber-300 space-y-0.5">
      {capped && <p>Showing the newest {SIDE_CAP} of each — type more digits to narrow.</p>}
      {roomsUnread && <p>Couldn't load the coldroom names, so a cell shows without its room.</p>}
    </div>
  )
}

// Where a pallet row stands (F09 UF-02) — the pallet page's own phrase, so the
// list and the page say the same thing. Left out where it would only repeat the
// status chip ("Dispatched" with no truck read) or the state names no place.
function Whereabouts({ hit, roomCode }: { hit: SearchHit; roomCode: (id: string) => string | null }) {
  if (hit.kind !== 'pallet') return null
  const where = palletWhereabouts(hit, { missionNumber: hit.missionNumber, roomCode, onTruck: true })
  if (!where || where === (STATUS_LABEL[hit.status] ?? hit.status)) return null
  return (
    <p className="text-xs text-fg-secondary flex items-center gap-1 min-w-0">
      <MapPin size={12} className="shrink-0 text-fg-muted" aria-hidden />
      <span className="truncate">{where}</span>
    </p>
  )
}

/** One result. Found but not openable by this viewer: plain text, no link —
 *  never left out, which read as "no match" (see searchHitAccess). */
function SearchHitRow({ hit, gate, roomCode, isActive, onOpen, onHover }: {
  hit: SearchHit; gate: (moduleKey: string) => boolean | null; roomCode: (id: string) => string | null; isActive: boolean
  onOpen: () => void; onHover: () => void
}) {
  const access = searchHitAccess(hit, gate)
  const trail = access === 'open' ? '→' : SHUT_ARROW[access]
  if (access !== 'open') return (
    <div data-testid="search-hit-unopenable"
      className={clsx('w-full text-left px-4 py-2.5 border-b hairline last:border-0 flex items-center gap-3',
        isActive && 'surface')}>
      <SearchHitBody hit={hit} trail={trail} roomCode={roomCode} />
    </div>
  )
  return (
    <button
      onClick={onOpen}
      onMouseEnter={onHover}
      aria-label={`Open ${hit.kind} ${hit.display} · ${STATUS_LABEL[hit.status] ?? hit.status}`}
      className={clsx(
        'w-full text-left px-4 py-2.5 border-b hairline last:border-0 flex items-center gap-3 transition-colors',
        isActive ? 'surface' : 'hover:bg-surface/60'
      )}
    >
      <SearchHitBody hit={hit} trail={trail} roomCode={roomCode} />
    </button>
  )
}

const SHUT_ARROW: Record<Exclude<SearchHitAccess, 'open'>, string> = {
  deciding: '…',
  closed:   "you can't open bins",
}

const KIND_CHIP: Record<SearchHit['kind'], string> = {
  bin:    'bg-cyan-900/60 text-cyan-200',
  pallet: 'bg-indigo-900/60 text-indigo-200',
}
const KIND_TEXT: Record<SearchHit['kind'], string> = {
  bin:    'text-cyan-200',
  pallet: 'text-white',
}

function SearchHitBody({ hit: r, trail, roomCode }: { hit: SearchHit; trail: string; roomCode: (id: string) => string | null }) {
  return (
    <>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={clsx('text-[9px] uppercase tracking-wider rounded px-1.5 py-0.5 font-bold shrink-0', KIND_CHIP[r.kind])}>
            {r.kind}
          </span>
          <p className={clsx('font-mono font-bold text-sm', KIND_TEXT[r.kind])}>{r.display}</p>
          <StatusPill object={r.kind === 'bin' ? 'bin' : 'pallet'} status={r.status} label={STATUS_LABEL[r.status]} />
        </div>
        <Whereabouts hit={r} roomCode={roomCode} />
        <p className="text-xs text-fg-muted truncate">
          {r.runLabel}
          {r.grade ? ` · ${formatGradeLabel(r.grade)}` : ''}
          {r.size_code ? ` · ${r.size_code}` : ''}
          {r.net_weight_kg ? ` · ${r.net_weight_kg.toFixed(1)} kg` : ''}
        </p>
      </div>
      <span className="text-xs text-fg-muted shrink-0">{trail}</span>
    </>
  )
}
